import IMlsDataVersions from '@/models/mls/IMlsDataVersions';
import MlsDataConverter from '@/converters/2.6/MlsDataConverter';
import HostUtils from '@/utils/HostUtils';
import IComp from '@/models/Comp';
import IExchangeCompAddress from '@/models/exchange/IExchangeCompAddress';
import IExchangeAddress from '@/models/exchange/IExchangeAddress';
import IOriginalValuesData from '@/models/original/IOriginalValuesData';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
import IMlsPropertyDataV1 from '@/models/mls/IMlsPropertyDataV1';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import IWorkFile from '@/models/fact-sheet/IWorkFile';
import ITokenData from '@/models/auth/ITokenData';
import IMlsCsvFileInfos from '@/models/mls/IMlsCsvFileInfos';
import IFactSheet from '@/models/fact-sheet/IFactSheet';
import ILookup from '@/models/common/ILookup';
import ISalesHistorySelections from '@/models/sales-history/ISalesHistorySelections';
import IExchangeReportFields from '@/models/exchange/IExchangeReportFields';
import { Actions, Getters, store } from '@/store';
import QSUtils from '@/utils/QSUtils';
import { forEach } from 'lodash';

enum DataInReportKeys {
    MLS = 'MlsImport',
    MLS2 = 'MlsImportv2',
    Original = 'OriginalValues',
    SalesHistorySelections = 'SalesHistorySelections',
}

enum PushToReportKeys {
    CSV = 'SaveCSV',
    ReportFieldData = 'ReportFieldData',
}

enum LaunchProcessKeys {
    Default = 'Default',
    Browser = 'Browser',
}

enum FactSheetKeys {
    Subject = 'DataFactSheetSubject',
    Comp = 'DataFactSheetComp',
}

/**
 * This service handles all the interaction with our Host application (TOTAL, etc...).
 * TOTAL provides a QuickSourceExchange class we use when the calling product id indicates.
 * Some methods within are private, the intention is to use the more convenient public method.
 */
export default class HostExchangeService {
    private static instance: HostExchangeService;

    private constructor() {
        // Needs to be private, avoid 'Unexpected empty constructor' error
    }

    static getInstance(): HostExchangeService {
        if (!this.instance) {
            this.instance = new HostExchangeService();
        }
        return this.instance;
    }

    /**
     * We've experienced an issue with availability of the QuickSourceExchange class upon launch.
     * This method checks for host is TOTAL and can make a call to QuickSourceExchange.
     * @returns false on TOTAL and call to QuickSourceExchange indicates a ReferenceError, true otherwise.
     */
    async isAvailable(): Promise<boolean> {
        if (HostUtils.isTotal()) {
            try {
                // Calling this just to chack for error - "ReferenceError: QuickSourceExchange is not defined"
                // @ts-ignore
                await QuickSourceExchange.GetDataFromReport(DataInReportKeys.MLS);
            } catch (error) {
                const message = `${error}`;
                //console.log(`HostExchangeService.haveHostAndIsAvailable(), Error[${error}]`);
                if (message.includes('ReferenceError') && message.includes('QuickSourceExchange')) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Get a new QS token data from our host. Includes the access token, refresh token
     * and expiration.
     * @returns
     */
    async requestQuickSourceToken(): Promise<ITokenData | null> {
        const prependBearer = false;

        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                const stringData = await QuickSourceExchange.GetJwtTokenInfo();
                if (stringData) {
                    const tokenData = JSON.parse(stringData) as ITokenData;
                    return tokenData;
                }
                console.log('HostExchangeService.requestQuickSourceToken(), No token data received.');
            } catch (error) {
                console.log(`HostExchangeService.requestQuickSourceToken(), Error[${error}]`);
            }
        }
        return null;
    }

    /**
     * Gets data from the report that was previously saved using PutDataInReport method
     * @returns The MLS data versions, where a version may be undefined.
     */
    async getMlsDataFromReport(): Promise<IMlsDataVersions> {
        const result: IMlsDataVersions = {};

        const mlsDataV1 = await this.getMlsDataFromReportV1();
        const mlsDataV2 = await this.getMlsDataFromReportV2();
        if (mlsDataV2 === null && mlsDataV1 != null) {
            result.propertyDataV1 = mlsDataV1;

            // No V2 data, so convert any V1 data to V2
            result.propertyData = MlsDataConverter.convertToV2(mlsDataV1);
        } else if (mlsDataV2 != null) {
            result.propertyData = mlsDataV2;

            // Convert V2 data to V1
            result.propertyDataV1 = MlsDataConverter.convertToV1(mlsDataV2);
        }

        return result;
    }

    /**
     * Get the old style (simple) MLS object or null if it doesn't exist.
     * @returns
     */
    private async getMlsDataFromReportV1(): Promise<IMlsPropertyDataV1 | null> {
        const data = await this.getDataFromReport(DataInReportKeys.MLS);
        if (data.length > 0) {
            const result: IMlsPropertyDataV1 = JSON.parse(data);
            return result;
        }
        return null;
    }

    /**
     * Get the new style (complex) MLS object or null if it doesn't exist.
     * @returns
     */
    private async getMlsDataFromReportV2(): Promise<IMLSPropertyData | null> {
        const data = await this.getDataFromReport(DataInReportKeys.MLS2);
        if (data.length > 0) {
            const result: IMLSPropertyData = JSON.parse(data);
            return result;
        }
        return null;
    }

    /**
     * Gets Original values from the report. Was previously saved using putOriginalValuesInReport method.
     * @returns The file content requested, or empty string when does not exist on host
     */
    async getOriginalValuesFromReport(): Promise<IOriginalValuesData | null> {
        const data = await this.getDataFromReport(DataInReportKeys.Original);
        if (data.length > 0) {
            const result: IOriginalValuesData = JSON.parse(data);
            return result;
        }
        return null;
    }

    /**
     * Gets Sales History Selections from the report. Was previously saved using putSalesHistorySelectionsInReport method.
     * @returns The file content requested, or empty string when does not exist on host
     */
    async getSalesHistorySelectionsFromReport(): Promise<ISalesHistorySelections[] | null> {
        const data = await this.getDataFromReport(DataInReportKeys.SalesHistorySelections);
        if (data.length > 0) {
            const result: ISalesHistorySelections[] = JSON.parse(data);
            return result;
        }
        return null;
    }

    /**
     * Gets data from the report that was previously saved using PutDataInReport method
     * @param tagName
     * @returns The file content requested, or empty string when does not exist on host
     */
    private async getDataFromReport(tagName: DataInReportKeys): Promise<string> {
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                return await QuickSourceExchange.GetDataFromReport(tagName);
            } catch (error) {
                console.log(`HostExchangeService.getDataFromReport(), Error[${error}] on tag[${tagName}]`);
            }
        }
        return '';
    }

    async exportFinalComps(compFinalData: ICompFinalData): Promise<void> {

        let stringData = '';
        if(QSUtils.debugger()) {
            console.log("exportFinalComps method");
            console.log("compFinalData", compFinalData);

            let totalLength = 0;
            for(let i = 0; i < compFinalData.length; i++) {
                console.log(`compFinalData ${i} raw: `, compFinalData[i]);


                // remove comp photos
                compFinalData[i]['propertyPhoto'] = '';
                const idx = compFinalData[i]['sourceData'].findIndex((f) => f.fieldName === 'COMP_PHOTO')
                if(idx > -1) {
                    compFinalData[i]['sourceData'][idx].value = '';
                }
                for(let j = 0; j < compFinalData[i]['sources'].length; j++) {
                    compFinalData[i]['sources'][j]['sourceData'] = {};
                }

                let stringify = JSON.stringify(compFinalData[i]);
                console.log(`compFinalData ${i} stringify: `, stringify);
                console.log(`compFinalData ${i} length: `, stringify.length);
                totalLength = totalLength + stringify.length;

                stringify = (i > 0) ? `,${stringify}`: stringify;
                stringData = stringData + stringify
            }
            
            stringData = `[${stringData}]`;

            console.log("totalLength", totalLength);
            console.log("stringData Debugging Final", stringData)
        } else {
            stringData = JSON.stringify(compFinalData);
            console.log("stringData NOT Debugging Final", stringData)
        }

        if(QSUtils.debugger()) {
            console.log("HostUtils.isTotal() result: ", HostUtils.isTotal());
        }

        if (HostUtils.isTotal()) {
            try {
                //const stringData = JSON.stringify(compFinalData);

                if(QSUtils.debugger()) {
                    console.log("stringData", stringData);
                }

                if(QSUtils.debugger()) {
                    console.log("HostUtils.getUpdateNumber() result: ", HostUtils.getUpdateNumber());
                    console.log("stringData result: ", stringData);

                    // @ts-ignore
                    console.log("QuickSourceExchange object: ", QuickSourceExchange);
                }

                if (HostUtils.getUpdateNumber() >= 248) {
                    if(QSUtils.debugger()) {
                        console.log("ExportFieldsToTotal IF Before", stringData);
                    }

                    // @ts-ignore
                    QuickSourceExchange.ExportFieldsToTotal(stringData, 2);
                } else {

                    if(QSUtils.debugger()) {
                        console.log("ExportFieldsToTotal ELSE Before", stringData);
                    }

                    // @ts-ignore
                    QuickSourceExchange.ExportFieldsToTotal(stringData);
                }
            } catch (error) {
                console.log(`HostExchangeService.exportFinalComps(), Error[${error}]`);
            }
        }
    }

    /**
     * Save MLS property data to a file in the Report (Total)
     * @param data
     */
    async putMlsDataInReport(data: IMLSPropertyData): Promise<boolean> {
        if (data) {
            if (HostUtils.isTotal() && HostUtils.getUpdateNumber() >= 232) {
                const stringData = JSON.stringify(data);
                return await this.putDataInReport(DataInReportKeys.MLS2, stringData);
            }
        }
        return true;
    }

    /**
     * Save Original values data to a file in the Report (Total)
     * @param data
     */
    async putOriginalValuesInReport(data: IOriginalValuesData): Promise<boolean> {
        if (data) {
            if (HostUtils.isTotal()) {
                const stringData = JSON.stringify(data);
                return await this.putDataInReport(DataInReportKeys.Original, stringData);
            }
        }
        return true;
    }

    /**
     * Save Sales History Selections data to a file in the Report (Total)
     * @param data
     */
    async putSalesHistorySelectionsInReport(data: ISalesHistorySelections[]): Promise<boolean> {
        if (data) {
            if (HostUtils.isTotal()) {
                const stringData = JSON.stringify(data);
                return await this.putDataInReport(DataInReportKeys.SalesHistorySelections, stringData);
            }
        }
        return true;
    }

    /**
     * Saves the given file content to a file in the Report with the given tagName as part of the file name (Total).
     * @param tagName
     * @param content
     * @returns true on successful save (or not Total), false on failure
     */
    private async putDataInReport(tagName: DataInReportKeys, content: string): Promise<boolean> {
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                const response = await QuickSourceExchange.PutDataInReport(tagName, content);

                // the only successful response is a blank string or 'success'
                if (response && response !== '' && response !== 'success') {
                    console.log(`HostExchangeService.putDataInReport(), Error response[${response}] on key[${tagName}]`);
                    return false;
                }
            } catch (error) {
                console.log(`HostExchangeService.putDataInReport(), Error[${error}]`);
                return false;
            }
        }
        return true;
    }

    /**
     * Push the MLS CSV info to the report.
     * @param csvFileInfo
     */
    async pushMlsCsvInfoToReport(csvFileInfos: IMlsCsvFileInfos): Promise<boolean> {
        if (csvFileInfos) {
            if (HostUtils.isTotal() && HostUtils.getUpdateNumber() >= 249) {
                const stringData = JSON.stringify(csvFileInfos);
                return await this.pushToReport(PushToReportKeys.CSV, stringData);
            }
        }
        return true;
    }

    /**
     * Push the Report Fields data info to the report.
     * Contains report level field data like CMPHIST_TXT1 (AnalysisOfPriorSale) and such.
     * @param csvFileInfo
     */
    async pushReportFieldDataToReport(reportFields: IExchangeReportFields): Promise<boolean> {
        if (reportFields) {
            if (HostUtils.isTotal()) {
                const stringData = JSON.stringify(reportFields);

                if(QSUtils.debugger()) {
                    console.log("pushReportFieldDataToReport method");
                    console.log("stringData result: ", stringData);
                }

                return await this.pushToReport(PushToReportKeys.ReportFieldData, stringData);
            }
        }
        return true;
    }

    /**
     *  Push the given content to the report with the given tag name
     * @param tagName
     * @param content
     */
    private async pushToReport(tagName: PushToReportKeys, content: string): Promise<boolean> {
        if (HostUtils.isTotal()) {
            try {

                if(QSUtils.debugger()) {
                    console.log("pushToReport method");
                    console.log("tagName result: ", tagName);
                    console.log("content result: ", content);
                }

                // @ts-ignore
                const response = await QuickSourceExchange.PushToReport(tagName, content);
                // the only successful response is "Success"
                if (!response || response.toLowerCase() !== 'success') {
                    console.log(`HostExchangeService.pushToReport(), Error response[${response}] on tag[${tagName}]`);
                    return false;
                }
            } catch (error) {
                console.log(`HostExchangeService.pushToReport(), Error[${error}] on tag[${tagName}]`);
                return false;
            }
        }
        return true;
    }

    /**
     *  Need to identify the parameter type(s)
     * @param workFileData
     */
    async placeDataFactSheetInWorkfile(workFile: IWorkFile): Promise<void> {
        if (HostUtils.isTotal()) {
            try {
                const stringData = JSON.stringify(workFile);

                // @ts-ignore
                await QuickSourceExchange.PlaceDataFactSheetInWorkfile(stringData);
            } catch (error) {
                console.log(`HostExchangeService.placeDataFactSheetInWorkfile(), Error[${error}]`);
            }
        }
    }

    /**
     * Add comp address with additional values to Total.
     * Note: The comp's compNumber value is important, should be the next in sequence.
     * @param compsGuid
     * @param comp
     */
    async addCompAddress(compsGuid: string, comp: IComp): Promise<void> {
        if (HostUtils.isTotal()) {
            const exchangeAddress: IExchangeAddress = {
                address: comp.address.address1,
                address2: comp.address.address2,
                unitNumber: comp.address.unitNumber,
                city: comp.address.city,
                state: comp.address.state,
                zipCode: comp.address.zipCode,
                compNumber: comp.compNumber,
            };
            const exchangeComp: IExchangeCompAddress = {
                compsGuid: compsGuid,
                addresses: exchangeAddress,
            };
            const data = JSON.stringify(exchangeComp);

            // @ts-ignore
            //await QuickSourceExchange.AddCompAddress(data);

            // @ts-ignore
            const response = JSON.parse(await QuickSourceExchange.AddCompAddressForVue(data));
            // Spoke with the Total Team and AddCompAddressForVue response will always be an array with only one comp.
            // So we will always use index of 0 of the array is not empty
            if (response.TotalComps.length > 0) {
                const compData = response.TotalComps[0];
                compData.compKey = response.CompsId;
                const comp = QSUtils.convertKeysToLowerCaseFirstLetter(compData);
                store.dispatch(Actions.CompAddresses.LocalDatabaseComps, comp);
            }
        }
    }

    /**
     * Get a base 64 string for the given guid when the host is Total.
     * @param guid
     * @returns a replacement string when the host is Total. The given guid when the host isn't Total.
     */
    async getBase64ForImage(guid: string): Promise<string> {
        let image = '';
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                image = await QuickSourceExchange.GetBase64ForImage(guid);
            } catch (error) {
                console.log(`HostExchangeService.getBase64ForImage(), Error[${error}]`);
            }
        }
        return image;
    }

    async sendDataFactSheet(factSheet: IFactSheet, isSubject: boolean): Promise<boolean> {
        const factSheetData: ILookup<IFactSheet> = {};
        factSheetData[isSubject ? FactSheetKeys.Subject : FactSheetKeys.Comp] = factSheet;

        const stringData = JSON.stringify(factSheetData);
        return await this.launchProcess(LaunchProcessKeys.Browser, stringData);
    }

    private async launchProcess(key: LaunchProcessKeys, data: string): Promise<boolean> {
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                const response = await QuickSourceExchange.LaunchProcess(key, data);

                // the only successful response is a 'Success' string
                if (response && response.toLowerCase() !== 'success') {
                    console.log(`HostExchangeService.launchProcess(), Error response[${response}] on key[${key}]`);
                    return false;
                }
            } catch (error) {
                console.log(`HostExchangeService.launchProcess(), Error[${error}] on key[${key}]`);
                return false;
            }
        }
        return true;
    }

    /**
     * Causes Total to query for raw Public Records data from the QuickSource API and place the response in cached memory.
     * The api is specified in alaredun and is something like '/api/v1.3/QuickSource/Raw/'.
     * Under debug will also write the response to a JSON file.
     * @param address1
     * @param zipCode
     */
    async getRawPublicRecordsDataForAddress(address1: string, zipCode: string): Promise<void> {
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                await QuickSourceExchange.GetRawPublicRecordsDataForAddress(address1, zipCode);
            } catch (error) {
                console.log(`HostExchangeService.getRawPublicRecordsDataForAddress(), Error[${error}]`);
            }
        }
    }

    async closeForm(): Promise<void> {
        if (HostUtils.isTotal()) {
            try {
                // @ts-ignore
                await QuickSourceExchange.CloseForm();
            } catch (error) {
                console.log(`HostExchangeService.closeForm(), Error[${error}]`);
            }
        }
    }
}
